/**
 * Keycloak OIDC bootstrap. PKCE S256 + check-sso (silent SSO via
 * hidden iframe); matches the realm/client configuration used by
 * relay-api's JWT validator.
 *
 * Hand-rolled around `keycloak-js` instead of `@react-keycloak/web`.
 * The latter is unmaintained (last release 2022), pre-dates React
 * 18+ effect semantics, and double-invokes `keycloak.init()` under
 * any modern setup that re-mounts components — which corrupts the
 * PKCE code_verifier in sessionStorage and produces an infinite
 * `#state=…` redirect loop on callback. A single module-level
 * promise around `init()` is the simplest fix.
 *
 * Contract with `api/client.ts`:
 *   - This provider calls `setTokenProvider(...)` once so every
 *     openapi-fetch request picks up a fresh, auto-renewed token.
 *   - On any 401 the API client emits `relay:auth-expired`; we
 *     listen and re-trigger `kc.login()` — unless the session has
 *     ended (see `sessionEvents.ts`), where the v2 notice waits for
 *     the operator to choose "sign in again".
 */

import Keycloak from 'keycloak-js';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { setTokenProvider } from '@/api/client';
import {
  endSession,
  refreshOrEnd,
  RELOGIN_EVENT,
  sessionEndReason,
  sessionHasEnded,
} from '@/auth/sessionEvents';
import { clearTokens, loadTokens, saveTokens } from '@/auth/tokenStore';
import { env } from '@/config/env';

interface KeycloakState {
  keycloak: Keycloak;
  initialized: boolean;
}

const KeycloakContext = createContext<KeycloakState | null>(null);

// Module-level guard: ensures `kc.init()` runs exactly once across
// React's dev double-mounts, fast refresh, and any concurrent
// renders. The same `Keycloak` instance is reused.
let kcSingleton: Keycloak | null = null;
let kcInitPromise: Promise<boolean> | null = null;

function getKeycloak(): Keycloak {
  if (!kcSingleton) {
    kcSingleton = new Keycloak({
      url: env.keycloak.url,
      realm: env.keycloak.realm,
      clientId: env.keycloak.clientId,
    });
  }
  return kcSingleton;
}

function ensureInitialized(kc: Keycloak): Promise<boolean> {
  if (!kcInitPromise) {
    // Notes on the option choices:
    //   - No `onLoad`. With `check-sso` and no `silentCheckSsoRedirectUri`,
    //     keycloak-js falls back to a full top-level redirect with
    //     `prompt=none` on every mount — that produces the infinite
    //     `#state=…` loop. With `silentCheckSsoRedirectUri` set, the
    //     iframe it spawns runs its own PKCE flow inside the SPA's
    //     origin and mutates the verifier in `sessionStorage`, which
    //     races the real login callback and yields `invalid_code` on
    //     the token exchange. Skipping `onLoad` keeps init quiet:
    //     keycloak-js still auto-detects and processes the
    //     `#state=&code=` callback on its own, and `ProtectedRoute`
    //     calls `login()` explicitly when the user is unauthenticated.
    //   - `checkLoginIframe: false` skips the periodic third-party
    //     iframe SSO check; it adds nothing for a single-tab SPA and
    //     in dev (cross-port frame) it just spams cookie warnings.
    //   - `responseMode: 'fragment'` matches Keycloak's default for
    //     PKCE/public clients — the `?iss=` parameter is fine to
    //     ignore.
    //   - The tab's saved tokens (`tokenStore`): with them, a reload
    //     resumes with one token refresh instead of a redirect round
    //     trip through Keycloak and a second full app load. If they're
    //     stale, init rejects; we drop them and sign in as usual.
    const saved = loadTokens();
    kcInitPromise = kc
      .init({
        pkceMethod: 'S256',
        checkLoginIframe: false,
        responseMode: 'fragment',
        ...(saved ?? {}),
      })
      .catch((err) => {
        // Reset so the next mount can retry rather than wedging on a
        // permanently rejected promise.
        kcInitPromise = null;
        clearTokens();
        throw err;
      });
  }
  return kcInitPromise;
}

export function KeycloakProvider({ children }: { children: ReactNode }) {
  const keycloak = useMemo(getKeycloak, []);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let lastRefresh = Date.now();
    const remember = () => {
      lastRefresh = Date.now();
      saveTokens(keycloak);
    };
    keycloak.onAuthSuccess = remember;
    keycloak.onAuthRefreshSuccess = remember;
    // Keycloak refused the refresh token: the session is gone. Say why
    // (expired / idle / another device) instead of bouncing to the
    // login page.
    const ended = () => {
      clearTokens();
      const authTime = keycloak.tokenParsed?.auth_time;
      endSession(
        () => keycloak.login(),
        sessionEndReason({
          now: Date.now(),
          authTimeMs: authTime ? authTime * 1000 : null,
          lastRefreshMs: lastRefresh,
        }),
      );
    };
    // Refresh, retrying a failure that isn't Keycloak refusing the token
    // (offline after the laptop wakes, Keycloak restarting). Only a
    // refused token ends the session.
    const refresh = (minValidity: number) => refreshOrEnd(() => keycloak.updateToken(minValidity), ended);
    setTokenProvider(async () => {
      if (!keycloak.authenticated || sessionHasEnded()) return undefined;
      // Still unreachable after the retries: send what we have; the
      // request fails like any other while offline, the session stays.
      await refresh(30);
      return keycloak.token ?? undefined;
    });
    ensureInitialized(keycloak)
      .then(() => {
        if (!cancelled) setInitialized(true);
      })
      .catch((err) => {
        // Surfaces in the console; kept non-fatal so the rest of the
        // tree renders the unauthenticated branch.
        console.error('keycloak init failed', err);
        if (!cancelled) setInitialized(true);
      });
    keycloak.onTokenExpired = () => {
      void refresh(30);
    };
    return () => {
      cancelled = true;
    };
  }, [keycloak]);

  useEffect(() => {
    // A 401 after the session ended is expected; the notice is up and
    // waits for "sign in again" (RELOGIN_EVENT).
    const onExpired = () => {
      if (!sessionHasEnded()) keycloak.login();
    };
    const onRelogin = () => {
      clearTokens();
      keycloak.login();
    };
    window.addEventListener('relay:auth-expired', onExpired);
    window.addEventListener(RELOGIN_EVENT, onRelogin);
    return () => {
      window.removeEventListener('relay:auth-expired', onExpired);
      window.removeEventListener(RELOGIN_EVENT, onRelogin);
    };
  }, [keycloak]);

  const value = useMemo(() => ({ keycloak, initialized }), [keycloak, initialized]);

  // Gate the rest of the tree on `initialized`. Otherwise downstream
  // queries (useMe, useFeatureFlags) fire before `kc.init()` resolves,
  // ship without an Authorization header, hit 401, and the api
  // client's `relay:auth-expired` listener turns that into another
  // `kc.login()` — racing the legitimate post-login token exchange
  // and producing an infinite `#state=…` redirect loop in dev.
  if (!initialized) {
    return null;
  }
  return <KeycloakContext.Provider value={value}>{children}</KeycloakContext.Provider>;
}

/** Drop-in for the old `@react-keycloak/web` hook. */
export function useKeycloak() {
  const ctx = useContext(KeycloakContext);
  if (!ctx) {
    throw new Error('useKeycloak called outside KeycloakProvider');
  }
  return ctx;
}
